import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import { DashboardLayout, AmountCard } from '../common/page_component/index.js';
import Loader from "../common/Loader.js" 
import Alert from '../common/Alert.js';
import DocumentModal from '../common/DocumentModal.js';
import userService from '../../services/UserService.js';
import mlAgentCommissionService from '../../services/mlAgentCommissionService.js';
import { canAccessUserRecord, canEditUserRecord } from '../../utils/companyAccess.js';
import { hasMlAgentCommissionAccess } from '../../utils/networkMode.js';
import {
  Users, Pencil, ArrowLeft, FileText, Wallet, Eye,
  Mail, Phone, MapPin, Building2, Shield, CheckCircle2, XCircle,
} from "lucide-react";
import Button from '../common/Button.js';
import { getUserPartyLabel, showUserPartyLabel } from '../../utils/userCreatableRoles.js';
import { useTranslation } from 'react-i18next';


const InfoField = ({ label, value, className = '' }) => (
  <div className={`bg-gray-50 p-4 rounded-xl border border-gray-200 ${className}`}>
    <p className="text-xs text-gray-500 mb-1">{label}</p>
    <p className="text-sm font-semibold text-gray-900 break-all">{value ?? 'N/A'}</p>
  </div>
);

const ViewUser = () => {
  const { t } = useTranslation();
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commission, setCommission] = useState(null);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchUser();
  }, [userId]);

  useEffect(() => {
    if (!user || user.role_name !== 'Agent') {
      setCommission(null);
      return;
    }
    const isSelf = String(currentUser?.user_id) === String(user.user_id);
    const canViewCommission = hasMlAgentCommissionAccess(currentUser) || isSelf;
    if (!canViewCommission || currentUser?.network_mode === 'EXTERNAL') {
      setCommission(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await mlAgentCommissionService.getSummary({
          agent_user_id: user.user_id,
        });
        if (!cancelled) setCommission(res?.data || null);
      } catch (err) {
        console.error(err);
        if (!cancelled) setCommission(null);
      }
    })();
    return () => { cancelled = true; };
  }, [user, currentUser]);

  const fetchUser = async () => {
    try {
      const payload = await userService.getById(userId);
      const userData = payload?.data || payload;
      if (!canAccessUserRecord(userData)) {
        Alert.error('You do not have access to this user');
        navigate('/users');
        return;
      }
      setUser(userData);
    } catch (err) {
      Alert.error('Failed to load user details');
      console.error(err);
      navigate('/users');
    } finally {
      setLoading(false);
    }
  };

  const getProfileImageUrl = () => {
    return user?.profile_photo_url || null;
  };

  const getDocumentUrl = () => {
    return user?.document_attachment_url || null;
  };

  const openDocumentModal = () => {
    setIsDocModalOpen(true);
  };

  const closeDocumentModal = () => {
    setIsDocModalOpen(false);
  };

  const capitalizeStatus = (status) => {
    return status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : '';
  };

  const getStatusBadge = (status) => {
    const isActive = status?.toLowerCase() === 'active';
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
        isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
      }`}>
        {isActive ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
        {capitalizeStatus(status)}
      </span>
    );
  };

  const formatSalaryAmount = (amount, salaryType) => {
    if (!amount) return 'N/A';
    const formatted = `₹ ${parseFloat(amount).toLocaleString('en-IN')}`;
    return salaryType === 'daily_wage' ? `${formatted} / day` : formatted;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const getPaymentStatusBadge = (status) => {
    const statusColors = {
      'PAID': 'bg-green-100 text-green-700',
      'PARTIAL': 'bg-orange-100 text-orange-700',
      'PENDING': 'bg-red-100 text-red-700'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusColors[status] || 'bg-gray-100 text-gray-700'}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <Loader fullScreen={false} message={t('common.loading') || 'Loading user...'} />
      </DashboardLayout>
    );
  }

  if (!user) {
    return (
      <DashboardLayout>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center text-gray-500">
          User not found
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {/* Page Header */}
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/users')} title={t('actions.backToUsers')}>
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </Button>
              <div className="p-2 bg-orange-100 rounded-xl">
                <Users className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-700">User Details</h2>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">View complete user information</p>
              </div>
            </div>
            {user && canEditUserRecord(user, currentUser) && (
              <Button variant="primary" icon={Pencil} onClick={() => navigate(`/users/edit/${userId}`)}>
                Edit User
              </Button>
            )}
          </div>
        </div>

        <div className="p-4 sm:p-6 space-y-6">
          {/* User Overview */}
          <div className="bg-gradient-to-r from-orange-50 to-white border border-orange-100 rounded-xl p-6">
            <div className="flex flex-col md:flex-row gap-6">
              <div className="flex-shrink-0">
                {getProfileImageUrl() ? (
                  <img
                    src={getProfileImageUrl()}
                    alt={user.name}
                    className="w-32 h-32 object-cover border-2 border-orange-200 rounded-md shadow-sm"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-32 h-32 bg-orange-100 border-2 border-orange-200 rounded-md flex items-center justify-center">
                    <Users className="w-16 h-16 text-orange-400" />
                  </div>
                )}
              </div>

              <div className="flex-1 space-y-4">
                <div>
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <h3 className="text-2xl font-bold text-gray-900">{user.name}</h3>
                    {getStatusBadge(user.status)}
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                      {user.role_name}
                    </span>
                    {user.salary_type === 'daily_wage' && (
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">
                        Daily Wages
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 font-medium">User ID: {user.user_id}</p>
                  {user.company_name && (
                    <p className="text-sm text-gray-600">Company: {user.company_name}</p>
                  )}
                  {showUserPartyLabel(user.role_name) && (
                    <p className="text-sm text-gray-600">
                      {user.role_name === 'Agent' ? 'Agent Type' : 'Contract Party'}: {getUserPartyLabel(user)}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-2 gap-2 sm:gap-4">
                  <div className="flex items-start gap-2">
                    <Mail className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-gray-500">Email</p>
                      <p className="text-sm font-medium text-gray-900 break-all">{user.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Phone className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-gray-500">Contact</p>
                      <p className="text-sm font-medium text-gray-900">{user.contact}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-gray-500">Branch / Location</p>
                      <p className="text-sm font-medium text-gray-900">
                        {[user.city, user.state].filter(Boolean).join(', ') || 'N/A'}
                      </p>
                    </div>
                  </div>
                  {user.company_name && (
                    <div className="flex items-start gap-2">
                      <Building2 className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-500">{t('common.company')}</p>
                        <p className="text-sm font-medium text-gray-900">{user.company_name}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Basic Information */}
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-4 pb-2 border-b-2 border-gray-200 flex items-center gap-2">
              <FileText className="w-5 h-5 text-orange-600" />
              Basic Information
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
              <InfoField label="Username" value={user.username} />
              <InfoField label="Full Name" value={user.name} />
              <InfoField label="Email" value={user.email} />
              <InfoField label="Date of Birth" value={formatDate(user.dob)} />
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-4 pb-2 border-b-2 border-gray-200 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-orange-600" />
              Contact Information
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
              <InfoField label="Contact Number" value={user.contact} />
              <InfoField label="Branch" value={user.city} />
              <InfoField label="State" value={user.state} />
              <InfoField label="Location / Address" value={user.location} className="sm:col-span-2 lg:col-span-2" />
            </div>
          </div>

          {/* Document Information */}
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-4 pb-2 border-b-2 border-gray-200 flex items-center gap-2">
              <FileText className="w-5 h-5 text-orange-600" />
              Document Information
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
              <InfoField
                label="Document Type"
                value={user.document_type ? user.document_type.replace(/_/g, ' ').toUpperCase() : 'N/A'}
              />
              <InfoField label="Document Number" value={user.document_number} />
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 sm:col-span-2">
                <p className="text-xs text-gray-500 mb-2">Document Attachment</p>
                {user.has_document_attachment ? (
                  <Button size="sm" onClick={openDocumentModal}>
                    <Eye className="w-4 h-4" />
                    View Document
                  </Button>
                ) : (
                  <p className="text-sm text-gray-500">No document attached</p>
                )}
              </div>
            </div>
          </div>

          {user.role_name === 'Driver' && user.has_driving_license_attachment && (
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-4 pb-2 border-b-2 border-gray-200">
                Driving License
              </h3>
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 inline-block">
                <Button size="sm" onClick={() => window.open(user.driving_license_attachment_url, '_blank')}>
                  <Eye className="w-4 h-4" />
                  View Driving License
                </Button>
              </div>
            </div>
          )}

          {/* Role & Compensation */}
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-4 pb-2 border-b-2 border-gray-200 flex items-center gap-2">
              <Shield className="w-5 h-5 text-orange-600" />
              Role & Compensation
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
              <InfoField label="Role" value={user.role_name} />
              <InfoField
                label="Salary Type"
                value={user.salary_type === 'daily_wage' ? 'Daily Wages' : 'Fixed Monthly Salary'}
              />
              <InfoField
                label={user.salary_type === 'daily_wage' ? 'Daily Wage' : 'Monthly Salary'}
                value={formatSalaryAmount(user.monthly_salary, user.salary_type)}
              />
              <InfoField label="Account Status" value={capitalizeStatus(user.status)} />
              <InfoField
                label="Booking Commission"
                value={`${user.booking_commission ?? user.shipment_commission ?? 0}%`}
              />
              <InfoField
                label="Delivery Commission"
                value={`${user.delivery_commission ?? user.shipment_commission ?? 0}%`}
              />
              {user.role_name === 'Agent' && (
                <>
                  <InfoField
                    label="Calculate Commission"
                    value={user.commission_enabled ? 'Yes' : 'No'}
                  />
                  <InfoField
                    label="Pickup Man"
                    value={user.is_pickup_man ? 'Yes' : 'No'}
                  />
                  <InfoField
                    label="TDS on Commission"
                    value={`${user.tds_percentage ?? 0}%`}
                  />
                </>
              )}
            </div>
          </div>

          {commission && (
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-4 pb-2 border-b-2 border-gray-200 flex items-center gap-2">
                <Wallet className="w-5 h-5 text-orange-600" />
                Agent Commission
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                <AmountCard
                  label="Total Commission"
                  amount={commission.total_amount || 0}
                  icon={<Wallet className="w-5 h-5 text-white" />}
                  color="bg-teal-600"
                  subAmount={commission.paid_amount || 0}
                  subLabel="Paid"
                  subAmount2={commission.unpaid_amount || 0}
                  subLabel2="Unpaid"
                />
                <InfoField
                  label="Paid entries"
                  value={commission.paid_count ?? 0}
                />
                <InfoField
                  label="Unpaid entries"
                  value={commission.unpaid_count ?? 0}
                />
              </div>
              {hasMlAgentCommissionAccess(currentUser) && Number(commission.unpaid_amount || 0) > 0 && (
                <div className="mt-3">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => navigate('/company-settlement-approvals')}
                  >
                    Pay unpaid commission
                  </Button>
                </div>
              )}
            </div>
          )}

          
          
          
          {/* System Information */}
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-4 pb-2 border-b-2 border-gray-200">
              System Information
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
              <InfoField label="User ID" value={user.user_id} />
              <InfoField label="Created By" value={user.created_by} />
              <InfoField label="Created At" value={formatDate(user.created_at)} />
              <InfoField label="Last Updated" value={formatDate(user.updated_at)} />
            </div>
          </div>
        </div>
      </div>

      {/* Document Modal */}
      <DocumentModal
        isOpen={isDocModalOpen}
        onClose={closeDocumentModal}
        documentUrl={getDocumentUrl()}
        documentType={user.document_type}
      />
</DashboardLayout>
  );
};

export default ViewUser;
